import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import groupBy from "lodash/groupBy";
import sumBy from "lodash/sumBy";

import FileItem from "fe-shared/src/components/FileList/FileItem";
import CollapsableCard from "fe-shared/src/components/CollapsableCard";

import { downloadFile } from "fe-shared/src/helpers/files";

import {
  InvoiceSummaryDetails,
  InvoiceAdjustmentsDetails,
  UnidentifiedMaterialsBanner,
} from "..";

import { Invoice, InvoiceItemType, InvoiceTransaction } from "../../types";
import InvoiceTransactionInfoDetails from "../InvoiceInfoDetails/InvoiceTransactionInfoDetails";
import BalanceAdjustmentsDetails from "../BalanceAdjustmentsDetails";
import InvoiceFeeDetails from "../InvoiceFeeDetails";

import styles from "./InvoiceContentView.module.scss";

interface Props {
  invoice: Invoice;
}

const InvoiceContentView = ({ invoice }: Props) => {
  const { t } = useTranslation();
  const {
    subtotal,
    attachments,
    externalAdjustment,
    internalAdjustments = [],
    hstTax,
    grandTotal,
    isHSTExemptChecked,
    invoiceTarget,
    hstRegistrationNumber,
    transactions,
    invoiceFees,
    invoiceFeeSubTotal,
    transactionFeeSubTotal,
    unidentifiedMaterials,
    paymentBalanceAdjustments,
    balanceAdjustmentsSubTotal,
  } = invoice;
  const hasExternalAdjustment = !!externalAdjustment;
  const hasInternalAdjustment = !!internalAdjustments.length;
  const isWithTransactions = invoiceTarget === InvoiceItemType.Transaction;
  const isAdjustment = hasExternalAdjustment || hasInternalAdjustment;
  const hasInvoiceFees = !!invoiceFees.length;
  const hasBalanceAdjustments = !!paymentBalanceAdjustments.length;
  const adjustmentSum = useMemo(
    () =>
      (externalAdjustment?.amount || 0) +
      internalAdjustments.reduce((sum, item) => sum + item.amount, 0),
    [externalAdjustment, internalAdjustments]
  );

  const transactionsByMonth = useMemo(
    () =>
      groupBy(transactions, (transaction: InvoiceTransaction) =>
        new Date(transaction.completedDate).toLocaleString("en-CA", {
          month: "long",
        })
      ),
    [transactions]
  );
  const totalWeight = useMemo(() => sumBy(transactions, "weight"), [
    transactions,
  ]);

  return (
    <>
      {unidentifiedMaterials && (
        <UnidentifiedMaterialsBanner materials={unidentifiedMaterials} />
      )}
      <h2 className={styles.invoiceHeaderTitle}>{t("invoices.summary")}</h2>
      <InvoiceSummaryDetails
        subtotal={subtotal}
        adjustmentSum={adjustmentSum}
        hstTax={hstTax}
        invoiceFeeSubTotal={invoiceFeeSubTotal}
        transactionFeeSubTotal={transactionFeeSubTotal}
        grandTotal={grandTotal}
        isHSTExemptChecked={isHSTExemptChecked}
        isWithTransactions={isWithTransactions}
        hstRegistrationNumber={hstRegistrationNumber}
        totalWeight={totalWeight}
        paymentBalanceSubTotal={balanceAdjustmentsSubTotal}
      />
      <hr />
      <h2 className={styles.invoiceHeaderTitle}>
        {t("invoices.details.title")}
      </h2>
      {(Object.entries(transactionsByMonth) as [string, InvoiceTransaction[]][]).map(
        ([month, transactionsOfMonth]) => {
          return (
            <CollapsableCard
              key={month}
              id={month}
              headerTitle={`${month} (${transactionsOfMonth.length})`}
              opened={false}
              className={styles.invoiceCard}
            >
              <InvoiceTransactionInfoDetails
                transactions={transactionsOfMonth}
              />
            </CollapsableCard>
          );
        }
      )}
      {isAdjustment && (
        <InvoiceAdjustmentsDetails
          externalAdjustment={externalAdjustment}
          internalAdjustments={internalAdjustments}
          adjustmentSum={adjustmentSum}
        />
      )}
      <div className={styles.invoiceModificators}>
        {hasInvoiceFees && <InvoiceFeeDetails invoiceFees={invoiceFees} />}
        {hasBalanceAdjustments && (
          <BalanceAdjustmentsDetails
            balanceAdjustments={paymentBalanceAdjustments}
          />
        )}
      </div>
      {!!attachments.length && (
        <>
          <h2 className={styles.invoiceHeaderTitle}>
            {t("invoices.supportingDocuments")}
          </h2>
          <div>
            {attachments?.map(({ documentPath, ...rest }) => (
              <FileItem
                disabled
                isViewOnly
                isDownloadActive
                id={documentPath}
                key={documentPath}
                onDownload={(id, fileName) => {
                  downloadFile(documentPath, fileName);
                }}
                {...rest}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default InvoiceContentView;
